/**
 * CreateOrderModal — Admin form to create a new MANUAL billing order.
 *
 * @see AC-1 through AC-5 in docs/specifications/2026-04-22-admin-order-creation-ui/spec.md
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTenantsQuery } from "../../../../hooks/useTenants";
import {
  useProductsQuery,
  useCreateBillingOrderMutation,
  useBillingOrdersQuery,
  useBillingSubscriptionsQuery,
  useCancelBillingOrderMutation,
  useLineDescriptionPreviewQuery,
} from "../../../../hooks/useAdminProductCatalog";
import { useAdminSubscriptionDetailsQuery } from "../../../../hooks/useAdminSubscriptions";
import { useDebounce } from "../../../../hooks/useDebounce";
import type { BillingProduct } from "../../../../types/adminBilling";
import type { SubscriptionDetailsResponse } from "../../../../types/adminSubscriptions";
import { ACTIVE_SUBSCRIPTION_STATUSES } from "../../../../constants/subscription.constants";
import { getTriggerLabel } from "../../../../types/billing-labels";
import { OrderStatusBadge, type OrderStatus } from "../../../../components/billing/OrderStatusBadge";
import { todayInWarsaw, formatDate } from "../utils/orderFormatters";

// =============================================================================
// CONSTANTS
// =============================================================================

/** Sentinel value used in <select> to represent null payment method */
const NO_PAYMENT_METHOD = "__null__" as const;

/** Sort order for single-line orders */
const SINGLE_LINE_SORT_ORDER = 0;

/** Maximum products fetched for the product picker */
const MAX_PRODUCTS_FOR_PICKER = 100;

/** Statuses treated as "open" for the AC-6 collision check (mirrors backend SLOT_BLOCKING_STATUSES). */
const OPEN_ORDER_STATUSES: OrderStatus[] = ["DRAFT", "CONFIRMED"];

/** Fixed cancel reason for the one-click "Anuluj ARN" collision CTA (AC-6, min. 10 znaków). */
const CANCEL_ARN_COLLISION_REASON =
  "Kolizja z nowym zamówieniem — anulowano z panelu tworzenia zamówień.";

// =============================================================================
// TYPES
// =============================================================================

type BillingInterval = "monthly" | "quarterly" | "yearly";
type PaymentMethod = "BANK_TRANSFER" | "STRIPE" | null;

interface FormErrors {
  tenantId?: string;
  productId?: string;
  billingInterval?: string;
  periodStart?: string;
  customDurationDays?: string;
  description?: string;
  _submit?: string;
}

export interface CreateOrderModalProps {
  brandId: string;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Optional tenant id to preselect (e.g. when opened from `/admin/tenants/:id`).
   * When provided, the tenant combobox is initialized with this id; if the
   * matching tenant is loaded its name fills the search input.
   * @see AC-O7
   */
  preselectTenantId?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

/** Label for interval display */
const INTERVAL_LABELS: Record<BillingInterval, string> = {
  monthly: "miesięczny",
  quarterly: "kwartalny",
  yearly: "roczny",
};

/** Default number of days for first custom period toggle */
const DEFAULT_CUSTOM_DAYS = 14;

/** Tooltip text for custom period checkbox */
const CUSTOM_PERIOD_TOOLTIP =
  "Pierwszy cykl ma niestandardową długość. Od drugiego cyklu system automatycznie odnawia subskrypcję na pełny okres zgodny z wybranym interwałem.";

/** Billing statuses for which fulfillment extends the subscription (inheriting its auto-renewal). */
const LIVE_BILLING_SUBSCRIPTION_STATUSES = ["ACTIVE", "TRIALING"];

/**
 * AC-5: the tenant's active billing-subscription period end, when it should
 * drive the "Okres od" prefill — `source === 'billing'`, an active status, and
 * `currentPeriodEnd` after today. Returns `null` otherwise (no subscription,
 * legacy source, inactive status, or period already ended).
 */
const getActiveSubscriptionPeriodEndDay = (
  subscriptionDetails: SubscriptionDetailsResponse | undefined,
  today: string
): string | null => {
  const subscription = subscriptionDetails?.subscription;
  const periodEndDay = subscription?.currentPeriodEnd?.slice(0, 10);
  const isActive =
    subscriptionDetails?.source === "billing" &&
    !!subscription &&
    (ACTIVE_SUBSCRIPTION_STATUSES as readonly string[]).includes(subscription.status) &&
    !!periodEndDay &&
    periodEndDay > today;
  return isActive ? (periodEndDay as string) : null;
};

/**
 * Upper bound (inclusive) for the custom first-cycle duration, in days (~2 years).
 * Mirrors backend `MAX_CUSTOM_DURATION_DAYS` (billing-interval.utils.ts) — keep in sync.
 */
const MAX_CUSTOM_DURATION_DAYS = 730;

/** Validate customDurationDays value — returns error message or undefined */
const validateCustomDays = (value: string): string | undefined => {
  // Reject scientific notation ("1e2"), floats, and non-digit strings (LOW #5)
  if (!/^\d+$/.test(value.trim())) {
    return "Podaj liczbę całkowitą";
  }
  const num = parseInt(value, 10);
  if (num < 1) return "Minimalna liczba dni to 1";
  if (num > MAX_CUSTOM_DURATION_DAYS)
    return `Maksymalna liczba dni to ${MAX_CUSTOM_DURATION_DAYS}`;
  return undefined;
};

/** Debounce delay for the description-preview query inputs (Technical Constraints AC-7). */
const PREVIEW_DEBOUNCE_MS = 300;

/**
 * Earliest acceptable year for the preview `periodStart`. Guards against the
 * incomplete years (`0002-...`) a native `<input type="date">` emits while the
 * year digits are still being typed (AC-7 Technical Constraints) — those must
 * never trigger a preview request.
 */
const MIN_PREVIEW_YEAR = 2000;

/** `periodStart` must be a complete YYYY-MM-DD date with a plausible year to preview (AC-7). */
const isPeriodStartValidForPreview = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return parseInt(value.slice(0, 4), 10) >= MIN_PREVIEW_YEAR;
};

/** AC-9: message shown under "Opis" while auto-refresh is suspended by a manual edit. */
const DESCRIPTION_EDIT_HINT =
  "Opis edytowany ręcznie — nie aktualizuje się automatycznie";

/** AC-7 edge case: shown when the preview request fails or times out. */
const DESCRIPTION_PREVIEW_ERROR =
  "Nie udało się pobrać podglądu opisu — opis zostanie wygenerowany automatycznie przy zapisie";

/** AC-11: shown when the catalog has no `invoiceDescription` and the server fell back. */
const DESCRIPTION_FALLBACK_HINT =
  "Brak opisu do faktury w katalogu dla tego produktu — użyto tekstu domyślnego. Sprawdź przed utworzeniem.";

// =============================================================================
// COMPONENT
// =============================================================================

export const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
  brandId,
  onClose,
  onSuccess,
  preselectTenantId,
}) => {
  // --- Data queries ---
  const {
    data: tenantsData,
    isLoading: isLoadingTenants,
    isError: isErrorTenants,
  } = useTenantsQuery();
  const {
    data: productsData,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useProductsQuery(brandId, { limit: MAX_PRODUCTS_FOR_PICKER });

  // Filter PACKAGE + ACTIVE products
  const activePackages: BillingProduct[] = (productsData?.data ?? []).filter(
    (p) => p.productType === "PACKAGE" && p.status === "ACTIVE"
  );

  const tenants = tenantsData ?? [];

  // --- Form state ---
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantId, setTenantId] = useState(preselectTenantId ?? "");

  // Populate tenantSearch once tenants are loaded and we have a preselect id.
  // Runs once because subsequent renders won't have empty tenantSearch with
  // a matching preselectTenantId after the first sync.
  useEffect(() => {
    if (!preselectTenantId || tenantSearch) return;
    const match = (tenantsData ?? []).find((t) => t.id === preselectTenantId);
    if (match) setTenantSearch(match.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantsData, preselectTenantId]);

  const [productId, setProductId] = useState("");
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [periodStart, setPeriodStart] = useState(() => todayInWarsaw());
  // AC-5: true once the admin manually edits "Okres od" — prefill must not overwrite it.
  const [periodStartDirty, setPeriodStartDirty] = useState(false);
  // AC-9: `description` only holds the manually-typed text; while !descriptionDirty
  // the displayed value is derived straight from the preview query (never copied
  // into state), so a stale response can never overwrite it (no sync useEffect).
  const [description, setDescription] = useState("");
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("BANK_TRANSFER");
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  // --- Custom period state (AC-6) ---
  /** Whether the "Niestandardowa długość" checkbox is checked */
  const [customDaysEnabled, setCustomDaysEnabled] = useState(false);
  const customDaysInputRef = useRef<HTMLInputElement>(null);
  /**
   * The preserved days value — stored even when checkbox is unchecked
   * so it can be restored when re-checked (M-UX-3 reset state preservation).
   */
  const [customDurationDaysInput, setCustomDurationDaysInput] = useState(
    String(DEFAULT_CUSTOM_DAYS)
  );
  const [customDaysTouched, setCustomDaysTouched] = useState(false);

  /** Initial auto-renewal of a NEW subscription (trial invite rescue, review MEDIUM-7). */
  const [autoRenewal, setAutoRenewal] = useState(true);

  // --- Blur-first validation state (M15) ---
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // --- Combobox state ---
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  // --- Mutation ---
  const createOrderMutation = useCreateBillingOrderMutation();
  const isLoading = createOrderMutation.isPending;

  // --- AC-5: admin subscription lookup (prefill "Okres od") ---
  const { data: subscriptionDetails, isError: isSubscriptionError } =
    useAdminSubscriptionDetailsQuery(tenantId || undefined, {
      refetchOnWindowFocus: false,
    });
  const isLegacyTenant = subscriptionDetails?.source === "legacy";

  // --- AC-6: open-order and successor-subscription collision checks ---
  const { data: openOrdersData } = useBillingOrdersQuery(brandId, {
    tenantId,
    status: OPEN_ORDER_STATUSES,
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  });
  const {
    data: tenantSubscriptionsData,
    isPlaceholderData: isTenantSubscriptionsPlaceholder,
  } = useBillingSubscriptionsQuery(brandId, {
    tenantId,
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  });

  const openOrders = openOrdersData?.data ?? [];
  const tenantSubscriptions = tenantSubscriptionsData?.data ?? [];

  // The backend honours autoRenewal only for a NEW subscription (409 when the
  // tenant has a live one) — offer it only once the tenant's list is known.
  const canChooseAutoRenewal =
    !!tenantId &&
    !!tenantSubscriptionsData &&
    !isTenantSubscriptionsPlaceholder &&
    !tenantSubscriptions.some((s) =>
      LIVE_BILLING_SUBSCRIPTION_STATUSES.includes(s.status)
    );

  const hasScheduledSuccessor = tenantSubscriptions.some(
    (s) => s.status === "SCHEDULED"
  );
  // AC-6: an open AUTO_RENEWAL order blocks only once its proforma is issued
  // (ISSUED/SENT) — `hasProforma` comes straight from the list endpoint.
  const blockingArnOrder = openOrders.find(
    (o) => o.trigger === "AUTO_RENEWAL" && o.hasProforma
  );
  const isBlocked = hasScheduledSuccessor || !!blockingArnOrder;
  const warningOrders = openOrders.filter((o) => o.id !== blockingArnOrder?.id);

  const cancelArnMutation = useCancelBillingOrderMutation(
    blockingArnOrder?.id ?? "",
    brandId
  );
  const handleCancelArn = () => {
    if (!blockingArnOrder) return;
    cancelArnMutation.mutate(CANCEL_ARN_COLLISION_REASON);
  };

  // Reset the "dirty" prefill guard whenever the selected tenant changes (AC-5).
  const prevTenantIdRef = useRef(tenantId);
  useEffect(() => {
    if (prevTenantIdRef.current !== tenantId) {
      setPeriodStartDirty(false);
      prevTenantIdRef.current = tenantId;
    }
  }, [tenantId]);

  // AC-5: prefill "Okres od" from the tenant's active billing subscription
  // (or today, when there is none / it's not active / the fetch failed).
  const today = todayInWarsaw();
  const activeSubscriptionPeriodEndDay = getActiveSubscriptionPeriodEndDay(
    subscriptionDetails,
    today
  );
  useEffect(() => {
    if (!tenantId || periodStartDirty) return;
    setPeriodStart(activeSubscriptionPeriodEndDay ?? today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, activeSubscriptionPeriodEndDay, periodStartDirty]);

  // Auto-focus the days input when checkbox is enabled (Medium #7)
  useEffect(() => {
    if (customDaysEnabled) customDaysInputRef.current?.focus();
  }, [customDaysEnabled]);

  // --- AC-6/AC-7: live description + periodEnd preview from the server ---
  // Debounced here (not inside the hook) so the debounce is visible to a
  // mocked `useLineDescriptionPreviewQuery` in tests (Technical Constraints).
  const debouncedPeriodStart = useDebounce(periodStart, PREVIEW_DEBOUNCE_MS);
  const debouncedCustomDurationDaysInput = useDebounce(
    customDurationDaysInput,
    PREVIEW_DEBOUNCE_MS
  );
  const previewCustomDurationDays = customDaysEnabled
    ? parseInt(debouncedCustomDurationDaysInput, 10)
    : null;
  const isCustomDaysValidForPreview =
    !customDaysEnabled ||
    validateCustomDays(debouncedCustomDurationDaysInput) === undefined;
  const previewEnabled =
    !!productId &&
    isPeriodStartValidForPreview(debouncedPeriodStart) &&
    isCustomDaysValidForPreview;
  const preview = useLineDescriptionPreviewQuery(
    {
      brandId,
      productId,
      billingInterval,
      periodStart: debouncedPeriodStart,
      customDurationDays: previewCustomDurationDays,
    },
    { enabled: previewEnabled }
  );
  // AC-7: pending only while actively fetching AND no data yet for the current
  // key — never shows a result for different (previous) params.
  const isPreviewPending = !preview.data && preview.isFetching;
  const previewFailed = previewEnabled && preview.isError;
  // AC-7/AC-10: pending shows "—" (never a result for stale/previous params);
  // AC-9: while editing, the manual text always wins regardless of preview state.
  const displayedDescription = descriptionDirty
    ? description
    : isPreviewPending
    ? "—"
    : preview.data?.description ?? "";
  const displayedPeriodEnd = isPreviewPending ? "—" : preview.data?.periodEnd ?? "—";
  // The counter measures real content — the pending "—" placeholder counts as 0,
  // same as the empty field after a preview error.
  const descriptionCharCount =
    !descriptionDirty && isPreviewPending ? 0 : displayedDescription.length;
  // WCAG 4.1.3: announce the auto-description regardless of focus (like
  // "Koniec okresu") — only a real preview result, never the pending "—" nor
  // manually typed text (that would announce on every keystroke).
  const descriptionAnnouncement =
    !descriptionDirty && !isPreviewPending && preview.data?.description
      ? `Automatyczny opis: ${preview.data.description}`
      : "";

  // --- Escape-to-close ---
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (dropdownOpen) {
          setDropdownOpen(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, dropdownOpen]);

  // --- Close dropdown on outside click ---
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  // --- Filtered tenants for combobox ---
  const filteredTenants = tenants.filter((t) =>
    t.name.toLowerCase().includes(tenantSearch.toLowerCase())
  );

  // --- Combobox keyboard navigation ---
  const handleComboboxKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setDropdownOpen(true);
        setActiveIndex((i) =>
          i < filteredTenants.length - 1 ? i + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setDropdownOpen(true);
        setActiveIndex((i) =>
          i > 0 ? i - 1 : filteredTenants.length - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (dropdownOpen && activeIndex >= 0 && filteredTenants[activeIndex]) {
          const t = filteredTenants[activeIndex];
          setTenantId(t.id);
          setTenantSearch(t.name);
          setDropdownOpen(false);
          setActiveIndex(-1);
        }
      } else if (e.key === "Escape") {
        setDropdownOpen(false);
        setActiveIndex(-1);
      }
    },
    [dropdownOpen, activeIndex, filteredTenants]
  );

  // --- Scroll active item into view ---
  useEffect(() => {
    if (listboxRef.current && activeIndex >= 0) {
      const item = listboxRef.current.children[activeIndex] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  // --- Validation ---
  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (!tenantId) errs.tenantId = "Tenant jest wymagany";
    if (!productId) errs.productId = "Produkt jest wymagany";
    if (!billingInterval) errs.billingInterval = "Interwał jest wymagany";
    if (!periodStart) errs.periodStart = "Data początku okresu jest wymagana";
    // periodEnd is now always derived — no longer validated as user input (AC-6)
    if (customDaysEnabled) {
      const daysErr = validateCustomDays(customDurationDaysInput);
      if (daysErr) errs.customDurationDays = daysErr;
    }
    // AC-8: client-side "required" validation only applies once the admin has
    // manually edited the description — without edits the server composes it.
    if (descriptionDirty) {
      if (!description.trim()) {
        errs.description = "Opis jest wymagany";
      } else if (description.trim().length > 500) {
        errs.description = "Opis nie może przekraczać 500 znaków";
      }
    }
    return errs;
  };

  // --- Submit ---
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    createOrderMutation.mutate(
      {
        tenantId,
        brandId,
        paymentMethod,
        line: {
          productId,
          billingInterval,
          periodStart,
          // periodEnd intentionally omitted — backend derives it from customDurationDays or billingInterval (AC-3)
          // description intentionally omitted unless manually edited — the server composes it (AC-5, AC-8)
          ...(descriptionDirty ? { description: description.trim() } : {}),
          sortOrder: SINGLE_LINE_SORT_ORDER,
          ...(customDaysEnabled
            ? { customDurationDays: parseInt(customDurationDaysInput, 10) }
            : {}),
          // Sent only when switched off — the default path stays unchanged.
          ...(canChooseAutoRenewal && !autoRenewal ? { autoRenewal: false } : {}),
        },
      },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
        onError: () => {
          setErrors((prev) => ({
            ...prev,
            _submit: "Nie udało się utworzyć zamówienia. Spróbuj ponownie.",
          }));
        },
      }
    );
  };

  // Revalidate on change after first submit attempt
  useEffect(() => {
    if (submitted) {
      setErrors(validate());
    }
    // Celowo omijamy validate() w deps — zawiera state który jest już w deps.
    // Włączenie validate() powodowałoby nieskończoną pętlę rerenderów.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, productId, billingInterval, periodStart, description, descriptionDirty, submitted, customDaysEnabled, customDurationDaysInput]);

  const buildPeriodPreviewText = () => {
    const intervalLabel = INTERVAL_LABELS[billingInterval];
    const periodEndFromPreview = preview.data?.periodEnd;
    let previewText: string;
    if (customDaysEnabled) {
      const days = parseInt(customDurationDaysInput, 10);
      const validDays =
        !isNaN(days) && days >= 1 && days <= MAX_CUSTOM_DURATION_DAYS;
      previewText = validDays
        ? `Pierwszy cykl: ${formatDate(periodStart)} → ${formatDate(periodEndFromPreview)} (${days} dni). Następne cykle: co ${intervalLabel}.`
        : `Pierwszy cykl: ${formatDate(periodStart)} → — (— dni). Następne cykle: co ${intervalLabel}.`;
    } else {
      previewText = `Okres: ${formatDate(periodStart)} → ${formatDate(periodEndFromPreview)}. Odnowienia: co ${intervalLabel}.`;
    }
    return (
      <div
        data-testid="period-preview"
        aria-live="polite"
        className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2"
      >
        {previewText}
      </div>
    );
  };

  const noTenants = !isLoadingTenants && tenants.length === 0;
  const noProducts = !isLoadingProducts && activePackages.length === 0;

  // Helper: show field error when submitted or field touched
  const showError = (field: keyof FormErrors): string | undefined => {
    if (submitted || touched[field]) return errors[field];
    return undefined;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-order-modal-title"
        className="bg-white rounded-lg max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3
            id="create-order-modal-title"
            className="text-lg font-semibold text-neutral-900"
          >
            Nowe zamówienie
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Tenant — searchable combobox (AC-2) */}
          <div ref={comboboxRef}>
            <label
              htmlFor="order-tenant-search"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Tenant *
            </label>
            <div className="relative">
              <input
                id="order-tenant-search"
                type="text"
                role="combobox"
                aria-expanded={dropdownOpen}
                aria-haspopup="listbox"
                aria-autocomplete="list"
                aria-controls="order-tenant-listbox"
                aria-activedescendant={
                  activeIndex >= 0 && filteredTenants[activeIndex]
                    ? `tenant-option-${filteredTenants[activeIndex].id}`
                    : undefined
                }
                value={tenantSearch}
                onChange={(e) => {
                  setTenantSearch(e.target.value);
                  setDropdownOpen(true);
                  setActiveIndex(-1);
                  // Reset tenantId if search is cleared
                  if (!e.target.value) setTenantId("");
                }}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => handleBlur("tenantId")}
                onKeyDown={handleComboboxKeyDown}
                placeholder={
                  isLoadingTenants ? "Ładowanie..." : "Wyszukaj tenanta..."
                }
                disabled={isLoadingTenants || noTenants}
                className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  showError("tenantId")
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-neutral-300"
                } disabled:bg-neutral-50 disabled:text-neutral-500`}
              />
              {dropdownOpen && filteredTenants.length > 0 && (
                <ul
                  id="order-tenant-listbox"
                  ref={listboxRef}
                  role="listbox"
                  aria-label="Tenanci"
                  className="absolute z-10 mt-1 w-full bg-white border border-neutral-200 rounded-md shadow-lg max-h-52 overflow-y-auto"
                >
                  {filteredTenants.map((t, idx) => (
                    <li
                      key={t.id}
                      id={`tenant-option-${t.id}`}
                      role="option"
                      aria-selected={t.id === tenantId}
                      className={`px-3 py-2 text-sm cursor-pointer ${
                        idx === activeIndex
                          ? "bg-primary-50 text-primary-900"
                          : t.id === tenantId
                          ? "bg-neutral-100 text-neutral-900"
                          : "text-neutral-800 hover:bg-neutral-50"
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setTenantId(t.id);
                        setTenantSearch(t.name);
                        setDropdownOpen(false);
                        setActiveIndex(-1);
                      }}
                    >
                      {t.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* L5: Use tenantSearch directly — it always reflects the selected tenant name */}
            {tenantId && tenantSearch && (
              <p className="text-xs text-neutral-500 mt-1">
                Wybrany: <span className="font-medium">{tenantSearch}</span>
              </p>
            )}
            {noTenants && (
              <p className="text-xs text-amber-600 mt-1">
                Brak dostępnych tenantów
              </p>
            )}
            {/* M16: Show error when data loading fails */}
            {isErrorTenants && (
              <p className="text-sm text-red-600 mt-1">Błąd ładowania tenantów</p>
            )}
            {showError("tenantId") && (
              <p className="text-xs text-red-600 mt-1">{showError("tenantId")}</p>
            )}
          </div>

          {/* AC-5: legacy tenant — block the whole form, point to migration */}
          {tenantId && isLegacyTenant && (
            <div
              role="alert"
              className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
              Ten tenant korzysta jeszcze z subskrypcji spoza modułu billing
              (legacy). Nowe zamówienie można utworzyć dopiero po migracji
              tenanta — zobacz <code>docs/admin-operations.md</code>.
            </div>
          )}

          {/* AC-6: blocking collisions — SCHEDULED successor and/or open ARN with proforma */}
          {tenantId && !isLegacyTenant && isBlocked && (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-2"
            >
              <p>Nie można utworzyć nowego zamówienia — najpierw anuluj:</p>
              <ul className="list-disc list-inside">
                {hasScheduledSuccessor && (
                  <li>zaplanowaną (SCHEDULED) subskrypcję następczą tenanta</li>
                )}
                {blockingArnOrder && (
                  <li>
                    otwarte zamówienie automatycznego odnowienia{" "}
                    {blockingArnOrder.orderNumber} z wystawioną proformą
                  </li>
                )}
              </ul>
              {blockingArnOrder && (
                <button
                  type="button"
                  onClick={handleCancelArn}
                  disabled={cancelArnMutation.isPending}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {cancelArnMutation.isPending ? "Anuluję..." : "Anuluj ARN"}
                </button>
              )}
            </div>
          )}

          {/* AC-6: non-blocking warning for other open orders */}
          {tenantId && !isLegacyTenant && warningOrders.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-1">
              <p>Tenant ma inne otwarte zamówienia:</p>
              <ul className="list-disc list-inside space-y-0.5">
                {warningOrders.map((o) => (
                  <li key={o.id} className="flex items-center gap-2">
                    <span>
                      {o.orderNumber} — {getTriggerLabel(o.trigger ?? "")}
                    </span>
                    <OrderStatusBadge status={o.status as OrderStatus} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <fieldset disabled={isLegacyTenant} className="space-y-5 border-0 p-0 m-0 min-w-0">
          {/* Product (AC-2) */}
          <div>
            <label
              htmlFor="order-product"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Produkt (PACKAGE) *
            </label>
            <select
              id="order-product"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              onBlur={() => handleBlur("productId")}
              disabled={isLoadingProducts || noProducts}
              className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                showError("productId")
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-neutral-300"
              } disabled:bg-neutral-50 disabled:text-neutral-500`}
            >
              <option value="">
                {isLoadingProducts
                  ? "Ładowanie..."
                  : noProducts
                  ? "Brak dostępnych produktów"
                  : "Wybierz produkt..."}
              </option>
              {activePackages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {noProducts && !isLoadingProducts && (
              <p className="text-xs text-amber-600 mt-1">
                Brak aktywnych produktów typu PACKAGE
              </p>
            )}
            {/* M16: Show error when product data loading fails */}
            {isErrorProducts && (
              <p className="text-sm text-red-600 mt-1">Błąd ładowania produktów</p>
            )}
            {showError("productId") && (
              <p className="text-xs text-red-600 mt-1">{showError("productId")}</p>
            )}
          </div>

          {/* Billing interval (AC-2, AC-6) */}
          <div>
            <label
              htmlFor="order-interval"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              {customDaysEnabled
                ? "Interwał odnowienia (po pierwszym cyklu) *"
                : "Interwał rozliczeniowy *"}
            </label>
            <select
              id="order-interval"
              value={billingInterval}
              onChange={(e) =>
                setBillingInterval(e.target.value as BillingInterval)
              }
              onBlur={() => handleBlur("billingInterval")}
              className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                showError("billingInterval")
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-neutral-300"
              }`}
            >
              <option value="monthly">Miesięczny</option>
              <option value="quarterly">Kwartalny</option>
              <option value="yearly">Roczny</option>
            </select>
            {showError("billingInterval") && (
              <p className="text-xs text-red-600 mt-1">
                {showError("billingInterval")}
              </p>
            )}
          </div>

          {/* Custom period checkbox (AC-6) */}
          <div>
            <div className="flex items-center gap-2">
              <input
                id="custom-days-checkbox"
                type="checkbox"
                checked={customDaysEnabled}
                onChange={(e) => {
                  setCustomDaysEnabled(e.target.checked);
                  // Reset touched state on toggle so errors clear
                  setCustomDaysTouched(false);
                }}
                aria-controls="custom-days-input"
                className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
              />
              <label
                htmlFor="custom-days-checkbox"
                className="text-sm font-medium text-neutral-700 cursor-pointer select-none"
              >
                Niestandardowa długość pierwszego okresu
              </label>
              <button
                type="button"
                title={CUSTOM_PERIOD_TOOLTIP}
                aria-label={CUSTOM_PERIOD_TOOLTIP}
                className="inline-flex items-center justify-center w-4 h-4 text-xs text-neutral-500 border border-neutral-300 rounded-full cursor-help bg-transparent p-0"
              >
                ?
              </button>
            </div>

            {customDaysEnabled && (
              <div className="mt-3 ml-6">
                <label
                  htmlFor="custom-days-input"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Liczba dni
                </label>
                {(() => {
                  const customDaysError =
                    submitted || customDaysTouched
                      ? validateCustomDays(customDurationDaysInput)
                      : undefined;
                  return (
                    <>
                      <input
                        id="custom-days-input"
                        ref={customDaysInputRef}
                        type="number"
                        min={1}
                        max={MAX_CUSTOM_DURATION_DAYS}
                        step={1}
                        value={customDurationDaysInput}
                        onChange={(e) => {
                          setCustomDurationDaysInput(e.target.value);
                        }}
                        onBlur={() => setCustomDaysTouched(true)}
                        aria-invalid={!!customDaysError}
                        aria-describedby={customDaysError ? "custom-days-error" : undefined}
                        className={`w-40 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          customDaysError
                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                            : "border-neutral-300"
                        }`}
                      />
                      <p className="text-xs text-neutral-400 mt-0.5">
                        Liczba dni od daty rozpoczęcia. 30 dni ≠ miesiąc kalendarzowy.
                      </p>
                      {customDaysError && (
                        <p id="custom-days-error" className="text-xs text-red-600 mt-1">
                          {customDaysError}
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Auto-renewal of a new subscription (review MEDIUM-7) */}
          {canChooseAutoRenewal && (
            <div>
              <div className="flex items-center gap-2">
                <input
                  id="order-auto-renewal"
                  type="checkbox"
                  checked={autoRenewal}
                  onChange={(e) => setAutoRenewal(e.target.checked)}
                  aria-describedby="order-auto-renewal-help"
                  className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="order-auto-renewal"
                  className="text-sm font-medium text-neutral-700 cursor-pointer select-none"
                >
                  Auto-odnawianie (proforma 7 dni przed końcem)
                </label>
              </div>
              <p
                id="order-auto-renewal-help"
                className="text-xs text-neutral-500 mt-1 ml-6"
              >
                Odznacz, gdy subskrypcja ma się skończyć bez proformy (np. ratunek
                zaproszenia na okres próbny). Klient kupi, włączając „Automatyczne
                odnawianie” na stronie Subskrypcja.
              </p>
            </div>
          )}

          {/* Period start / end (AC-2, AC-3, AC-6) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="order-period-start"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                Okres od *
              </label>
              <input
                id="order-period-start"
                type="date"
                value={periodStart}
                onChange={(e) => {
                  setPeriodStart(e.target.value);
                  setPeriodStartDirty(true);
                }}
                onBlur={() => handleBlur("periodStart")}
                aria-invalid={!!showError("periodStart") || isSubscriptionError || undefined}
                aria-describedby={
                  [
                    activeSubscriptionPeriodEndDay ? "period-start-hint" : null,
                    isSubscriptionError ? "period-start-fetch-error" : null,
                    showError("periodStart") ? "period-start-error" : null,
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
                className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  showError("periodStart")
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-neutral-300"
                }`}
              />
              {activeSubscriptionPeriodEndDay && (
                <p id="period-start-hint" className="text-xs text-neutral-500 mt-1">
                  Aktywna subskrypcja do {formatDate(activeSubscriptionPeriodEndDay)} —
                  przedłużenie zaczyna się od tej daty
                </p>
              )}
              {isSubscriptionError && (
                <p id="period-start-fetch-error" className="text-xs text-amber-600 mt-1">
                  Nie udało się pobrać danych subskrypcji tenanta — ustawiono dzisiejszą datę
                </p>
              )}
              {showError("periodStart") && (
                <p id="period-start-error" className="text-xs text-red-600 mt-1">
                  {showError("periodStart")}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="period-end-output" className="block text-sm font-medium text-neutral-700 mb-1">
                Koniec okresu (auto)
              </label>
              {/* AC-6/AC-10: periodEnd is display-only — always derived from the preview, never editable */}
              <output
                id="period-end-output"
                aria-live="polite"
                aria-busy={isPreviewPending || undefined}
                className="block w-full border border-neutral-200 rounded-md px-3 py-2 text-sm text-neutral-600 bg-neutral-50"
              >
                {displayedPeriodEnd}
              </output>
              <p className="text-xs text-neutral-400 mt-0.5">
                Data końca obliczona automatycznie
              </p>
            </div>
          </div>

          {/* Period preview (AC-6) — always visible */}
          {buildPeriodPreviewText()}

          {/* Description (AC-2, AC-7, AC-9) */}
          <div>
            <label
              htmlFor="order-description"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Opis
            </label>
            <input
              ref={descriptionInputRef}
              id="order-description"
              type="text"
              value={displayedDescription}
              maxLength={500}
              aria-busy={(!descriptionDirty && isPreviewPending) || undefined}
              onChange={(e) => {
                setDescription(e.target.value);
                setDescriptionDirty(true);
              }}
              onBlur={() => handleBlur("description")}
              aria-describedby={
                [
                  descriptionDirty ? "description-edit-hint" : null,
                  previewFailed ? "description-preview-error" : null,
                  !descriptionDirty && preview.data?.invoiceDescriptionSource === "fallback"
                    ? "description-fallback-hint"
                    : null,
                  showError("description") ? "description-error" : null,
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              placeholder="np. Dostęp do KiedyNabory.pl Standard BUR - abonament kwartalny (okres 29.10.2026 - 29.01.2027)"
              className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                showError("description")
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-neutral-300"
              }`}
            />
            <span
              id="description-live-announcement"
              className="sr-only"
              aria-live="polite"
              aria-atomic="true"
            >
              {descriptionAnnouncement}
            </span>
            <p className="text-xs text-neutral-400 mt-0.5 text-right">
              {descriptionCharCount}/500
            </p>
            {/* AC-9: the click hides this button (display: none), which would drop
                focus to <body> — so the handler moves focus to the "Opis" field first. */}
            <div
              className={`flex items-center justify-between gap-2 mt-0.5 ${
                descriptionDirty ? "" : "hidden"
              }`}
            >
              {descriptionDirty && (
                <p id="description-edit-hint" className="text-xs text-neutral-500">
                  {DESCRIPTION_EDIT_HINT}
                </p>
              )}
              <button
                type="button"
                onClick={() => {
                  descriptionInputRef.current?.focus();
                  setDescriptionDirty(false);
                }}
                className="text-xs text-primary-600 hover:text-primary-800 font-medium whitespace-nowrap"
              >
                Przywróć automatyczny opis
              </button>
            </div>
            {previewFailed && (
              <p
                id="description-preview-error"
                role="alert"
                className="text-xs text-amber-600 mt-1"
              >
                {DESCRIPTION_PREVIEW_ERROR}
              </p>
            )}
            {!descriptionDirty && preview.data?.invoiceDescriptionSource === "fallback" && (
              <p id="description-fallback-hint" className="text-xs text-amber-600 mt-1">
                {DESCRIPTION_FALLBACK_HINT}
              </p>
            )}
            {showError("description") && (
              <p id="description-error" className="text-xs text-red-600 mt-0.5">
                {showError("description")}
              </p>
            )}
          </div>

          {/* Payment method (AC-2) */}
          <div>
            <label
              htmlFor="order-payment-method"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Metoda płatności
            </label>
            <select
              id="order-payment-method"
              value={paymentMethod ?? NO_PAYMENT_METHOD}
              onChange={(e) => {
                const val = e.target.value;
                setPaymentMethod(
                  val === NO_PAYMENT_METHOD ? null : (val as PaymentMethod)
                );
              }}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="BANK_TRANSFER">Przelew bankowy</option>
              <option value="STRIPE">Karta płatnicza (Stripe)</option>
              <option value={NO_PAYMENT_METHOD}>Brak (dla zamówień 0 PLN)</option>
            </select>
          </div>

          {/* Submit error (network/server failures) */}
          {errors._submit && (
            <p role="alert" className="text-sm text-red-600">
              {errors._submit}
            </p>
          )}
          </fieldset>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium transition-colors disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isLoading || noTenants || noProducts || isLegacyTenant || isBlocked}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center gap-2"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              Utwórz zamówienie
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
